import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CityCombobox } from '@/components/CityCombobox';
import { AUTH } from '@/constants/testIds';
import { useSEO } from '@/lib/useSEO';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { isNativeApp, openGoogleAuthNative } from '@/lib/native';
import { AppleSignInButton } from '@/components/AppleSignInButton';

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get('rol') === 'seller' ? 'seller' : 'buyer');
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', city: '' });
  const [loading, setLoading] = useState(false);
  const [legalDocs, setLegalDocs] = useState([]);
  const [acceptedSlugs, setAcceptedSlugs] = useState({});

  useSEO({
    title: role === 'seller'
      ? 'Registrarse como vendedor | AutoMarket Colombia'
      : 'Crear cuenta | AutoMarket Colombia',
    description: 'Regístrate en AutoMarket Colombia y comienza a comprar o vender repuestos automotrices en Colombia.',
    canonical: '/registro',
  });

  useEffect(() => {
    api.get('/legal/current').then(({ data }) => setLegalDocs(data.documents || [])).catch(() => {});
  }, []);

  const allAccepted = legalDocs.length > 0 && legalDocs.every((d) => acceptedSlugs[d.doc_slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!allAccepted) {
      toast.error('Debes aceptar todos los documentos obligatorios');
      return;
    }
    setLoading(true);
    try {
      const accepted_terms = legalDocs.map((d) => ({ doc_slug: d.doc_slug, version: d.version }));
      const user = await register({ ...form, role, accepted_terms });
      toast.success(`Cuenta creada: ${user.name}`);
      nav(role === 'seller' ? '/vendedor/onboarding' : '/comprador');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al registrar');
    } finally { setLoading(false); }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    sessionStorage.setItem('signup_role', role);
    if (isNativeApp()) { openGoogleAuthNative(); return; }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Cuenta nueva</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Regístrate</h1>
        <p className="mt-2 text-slate-600 text-sm">¿Ya tienes cuenta? <Link to="/login" data-testid={AUTH.switchLink} className="font-semibold text-blue-700 hover:underline">Ingresar</Link></p>

        <div className="mt-6 grid grid-cols-2 gap-2">
          <button
            data-testid={AUTH.roleBuyer}
            type="button"
            onClick={() => setRole('buyer')}
            className={`p-4 border text-left transition-colors ${
              role === 'buyer' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 hover:border-slate-400 bg-white'
            }`}
          >
            <div className="font-display font-bold">Comprador</div>
            <div className="text-xs mt-1 opacity-80">Acceso inmediato</div>
          </button>
          <button
            data-testid={AUTH.roleSeller}
            type="button"
            onClick={() => setRole('seller')}
            className={`p-4 border text-left transition-colors ${
              role === 'seller' ? 'border-orange-500 bg-orange-500 text-slate-900' : 'border-slate-300 hover:border-slate-400 bg-white'
            }`}
          >
            <div className="font-display font-bold">Vendedor</div>
            <div className="text-xs mt-1 opacity-80">Requiere aprobación</div>
          </button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <Input data-testid={AUTH.nameInput} required placeholder="Nombre completo" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
          <Input data-testid={AUTH.emailInput} required type="email" placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" />
          <Input data-testid={AUTH.passwordInput} required type="password" placeholder="Contraseña (mín. 6 caracteres)"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none" />
          <Input data-testid={AUTH.phoneInput} placeholder="Teléfono" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" />
          <CityCombobox
            testId="auth-city-combobox"
            value={form.city}
            onChange={(city) => setForm({ ...form, city })}
            placeholder="Ciudad"
          />

          {legalDocs.length > 0 && (
            <div className="border border-slate-200 bg-slate-50 p-4 space-y-2" data-testid="register-terms-block">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Aceptación obligatoria</p>
              {legalDocs.map((d) => (
                <label key={d.doc_slug} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    data-testid={`register-terms-${d.doc_slug}`}
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!acceptedSlugs[d.doc_slug]}
                    onChange={(e) => setAcceptedSlugs({ ...acceptedSlugs, [d.doc_slug]: e.target.checked })}
                  />
                  <span>
                    Acepto <Link to={`/legal/${d.doc_slug}`} target="_blank" className="font-semibold text-blue-700 hover:underline">{d.title}</Link>
                    <span className="text-xs text-slate-500"> (v{String(d.version || '1.0').replace(/-provisional$/i, '')})</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          <Button data-testid={AUTH.submitBtn} type="submit" disabled={loading || !allAccepted}
            className="w-full rounded-none bg-slate-900 hover:bg-slate-800 h-11 font-bold">
            {loading ? 'Creando cuenta...' : 'Crear cuenta'}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <div className="h-px flex-1 bg-slate-200" />
          <span>O</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Button data-testid={AUTH.googleBtn} onClick={googleSignup} variant="outline"
          className="w-full rounded-none border-slate-300 hover:bg-slate-50 h-11">
          Continuar con Google
        </Button>
        <div className="mt-3">
          <AppleSignInButton role={role} onSuccess={(u) => nav(u.role === 'seller' ? '/vendedor/onboarding' : '/comprador')} />
        </div>
      </div>
    </Layout>
  );
}
